import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import SubscriptionAction from "./subscription-action";
import { updateTaxExemption } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  NO_DISPLAY_DATA,
} from "constants/index";
import { SubDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

const UpdateTaxExemptionZuora = () => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [exemptCertificateId, setExemptCertificateId] = useState("");
  const [exemptEntityUseCode, setExemptEntityUseCode] = useState("");

  const { subData, setSubData } = useContext(SubDataContext);
  const { showError } = useContext(SnackbarContext);

  const currentTaxExempt = subData?.subscription?.taxExempt;

  const taxExemptDisplay = () => {
    if (currentTaxExempt === undefined) {
      return NO_DISPLAY_DATA;
    }
    return currentTaxExempt ? "True" : "False";
  };

  const isConfirmDisabled =
    isTaxExempt &&
    exemptCertificateId.trim() === "" &&
    exemptEntityUseCode.trim() === "";

  const resetForm = () => {
    setIsTaxExempt(false);
    setExemptCertificateId("");
    setExemptEntityUseCode("");
  };

  const handleUpdateTaxExemption = () => {
    setShowLoadingSpinner(true);
    updateTaxExemption(
      {
        organizationId: toUUID(roleId),
        isTaxExempt,
        exemptCertificateId: exemptCertificateId.trim() || undefined,
        exemptEntityUseCode: exemptEntityUseCode.trim() || undefined,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        if (subData?.subscription) {
          const newSubData = subData.clone();
          if (newSubData.subscription) {
            newSubData.subscription.taxExempt = isTaxExempt;
          }
          setSubData(newSubData);
        }
        setOpenSuccessAlert(true);
        resetForm();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const handleClose = (
    _event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  return (
    <React.Fragment>
      <SubscriptionAction title="Tax Exempt:" displayInfo={taxExemptDisplay()}>
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="change-tax-exempt-zuora-btn"
          >
            UPDATE TAX EXEMPTION
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Update Tax Exemption"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleUpdateTaxExemption()}
        onCancel={resetForm}
        disabled={isConfirmDisabled}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        width="md"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography>Tax Exempt:</Typography>
              </Grid>
              <Grid item>
                <Switch
                  checked={isTaxExempt}
                  onChange={(e) => setIsTaxExempt(e.target.checked)}
                  color="primary"
                  data-testid="tax-exempt-toggle"
                />
              </Grid>
              <Grid item>
                <Typography fontWeight="bold">
                  {isTaxExempt ? "TRUE" : "FALSE"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {isTaxExempt && (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  At least one of the following fields is required when setting
                  tax exempt to true.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Exempt Certificate ID"
                  value={exemptCertificateId}
                  onChange={(e) => setExemptCertificateId(e.target.value)}
                  fullWidth
                  size="small"
                  data-testid="exempt-certificate-id-input"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Exempt Entity Use Code"
                  value={exemptEntityUseCode}
                  onChange={(e) => setExemptEntityUseCode(e.target.value)}
                  fullWidth
                  size="small"
                  data-testid="exempt-entity-use-code-input"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
      >
        <Alert severity="success" variant="filled">
          Tax exemption status updated
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default UpdateTaxExemptionZuora;
